import "./styles.css";

import Grid from "@material-ui/core/Grid";
import ArrivedIcon from "@material-ui/icons/LocalShipping";
import { CoolerTypeSelect } from "components/inputs/CoolerTypeSelect";
import { CountryISOCodeInput } from "components/inputs/CountryISOCodeInput";
import { PlaceSelectInput } from "components/inputs/PlaceSelectInput";
import { useTerminalsContext } from "contexts/TerminalsProvider";
import { ExternalTerminalConnectionStatus } from "entities/ExternalTerminalConnectionStatus";
import union from "lodash/union";
import React from "react";
import {
	NumberInput,
	ReferenceInput,
	SelectInput,
	TextInput,
	maxLength,
	useDataProvider,
	usePermissions,
	useTranslate,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";
import { ColdStorageSection } from "resources/orders/forms/ColdStorageSection";
import { ExtraWorkSection } from "resources/orders/forms/ExtraWorkSection";
import { checkIsRankDisabled } from "utils/checker/order";
import { rankChoicesGetter } from "utils/getter";
import { isStorageModuleEnabled } from "utils/storageModule";

import { formatPhoneNumber } from "../../../../../components/fields/PhoneField";
import {
	FormGroupGrid,
	GridForm,
	GridInputMultiChanges as GridInput,
	MultiChangesStateEnum,
	isMultiDiff,
} from "../../../../../components/GridForm";
import {
	CompanySelect,
	CompanyText,
	DateInput,
	NumberInputEx,
	PhoneInput,
	TimeInput,
} from "../../../../../components/inputs";
import { CommodityInput } from "../../../../../components/inputs/CommodityInput";
import { OrderReferenceInput } from "../../../../../components/inputs/OrderReferenceInput";
import { OSFCUserSelectInput } from "../../../../../components/inputs/OSFCUserSelect";
import { PortInput } from "../../../../../components/inputs/PortInput";
import { SelectEnumInput } from "../../../../../components/inputs/SelectEnumInput";
import { SelectTextInput } from "../../../../../components/inputs/SelectTextInput";
import {
	SelectDisabledInput,
	SelectUppercaseInput,
} from "../../../../../components/inputs/SelectUppercaseInput";
import TerminalDatesInput from "../../../../../components/inputs/TerminalDatesInput";
import { ReadOnlyInput } from "../../../../../components/inputs/TextReadonlyInput";
import { YesNoInput } from "../../../../../components/inputs/YesNoInput";
import {
	LabelSource,
	OrderService,
	OrderStatus,
	OrderStatusEditOrderCustomerAndReference,
	OrderStatusEditOrderTerminal,
	UserRole,
	UserRoleByCompanyType,
	etdThirdAssigneeServices,
	perEligibleServices,
} from "../../../../../entities";
import HideableOrderField from "../../../../../entities/HideableOrderField";
import { useTerminalFieldVisibility } from "../../../../../hooks/useTerminalFieldVisibility";
import { isNullUndEmpty } from "../../../../../utils/general";
import { validatePlaceLength } from "../../../../../utils/validation/order";
import { requiredIfNoFieldValue } from "../../../../../utils/validation/requiredIfNoFieldValue";
import { LoadingOrderInput } from "../../../forms/inputs/LoadingOrderInput";
import { PrintedDocsInput } from "../../../forms/inputs/PrintedDocsInput";
import { ServiceInput } from "../../../forms/inputs/ServiceInput";
import { TransporterInput } from "../../../forms/inputs/TransporterInput";
import { TripNumberInput } from "../../../forms/inputs/TripNumberInput";
import { PlumSealSection } from "../../../forms/PlumSealSection";
import {
	ArrivedPopup,
	DangerTextInputForInternalNotes,
	ETAETDFormGroup,
	ResponsiveIconButton,
	etaEtdPorts,
	useEtaEtdSync,
	useXPartyChange,
} from "../../../forms/shared";

const plugFunc = () => {};

const rowGroupStyle = {
	borderBottom: "2px solid #bdbdbd",
	paddingBottom: 4,
	marginBottom: 4,
};

export const ETAETDBlock = ({
	isAddMode,
	prefix,
	title,
	requiredMulti,
	isFieldHidden,
	isColdStorageService,
	isOutgoingPlukk,
	handleAreaChange,
	handleLabelSourceChange,
	onIsDocForNfsaChange,
	...props
}) => {
	const { form } = useForm();
	const { values } = useFormState();
	const byTerminalFilter = { terminal_id: [values["terminal_id"]] };
	const { data: terminals } = useTerminalsContext();
	const currentTerminal = terminals[values["terminal_id"]];
	const usesStorageModule = isStorageModuleEnabled(currentTerminal);
	const order_storage_place_field = usesStorageModule ? "storage_place_id" : "area_id";

	const externalTerminalFilter = {
		status: ExternalTerminalConnectionStatus.connected,
	};
	const externalTerminalDisabled = prefix === "eta" || !currentTerminal;
	if (currentTerminal) {
		externalTerminalFilter["own_terminal_guid"] = [terminals[values["terminal_id"]].guid];
	}

	const [
		handle3dPartyChangeOnTruck,
		handle3dPartyChangeOnTrailer,
		handleXPartyChangeOnDriver,
		handleXPartyChangeOn4pl,
	] = useXPartyChange(prefix);
	const handleEtaEtdSync = useEtaEtdSync(prefix);

	const curr_3rd_party_ids =
		isNullUndEmpty(values[`${prefix}_3rd_party`]) &&
		props.multiData.recordData.state[`${prefix}_3rd_party`] & MultiChangesStateEnum.IS_INIT_DIFF
			? props.multiData.recordData.multiValues[`${prefix}_3rd_party`]
			: [values[`${prefix}_3rd_party`]];
	const isDisabledForEditing = curr_3rd_party_ids.some((x) => !isNullUndEmpty(x));

	const isIntoPlukkStorage = values.service === OrderService.intoPlukkStorage;
	const isCrossDock = values.service === OrderService.crossDock;

	const dateRequiredValueKeyPrefix = prefix === "eta" ? "etd" : "eta";
	const portKey = etaEtdPorts[prefix];

	const currentTransportFilter = {
		no_range: true,
		is_deleted: false,
		company_id: 0,
	};
	const currentTransportSort = { field: "name", order: "ASC" };

	const currentDriverFilter = {
		no_range: true,
		is_blocked: false,
		is_deleted: false,
		company_id: 0,
	};
	const currentDriverSort = { field: "first_name", order: "ASC" };

	const arrivedPopupAnchorEl = React.useRef(null);
	const arrivedPopupProps = {
		...props,
		disabled: props.disabled || (props.enabledFields && !props.enabledFields[props.source]),
		prefix,
		arrivedPopupAnchorEl,
		isArrived: `is_${prefix}_arrived`,
		timeKey: `${prefix}_time`,
		slotTimeKey: `${prefix}_slot_time`,
		portKey,
		byTerminalFilter: byTerminalFilter,
	};
	if (
		!arrivedPopupProps.disabled &&
		props.enabledFields &&
		(!props.enabledFields[arrivedPopupProps.timeKey] ||
			!props.enabledFields[arrivedPopupProps.slotTimeKey] ||
			!props.enabledFields[arrivedPopupProps.portKey])
	) {
		arrivedPopupProps.disabled = true;
	}
	const placePrefix = prefix === "eta" ? "from" : "to";

	const handle3dPartyChange = (e) => {
		handle3dPartyChangeOnTruck(e);
		handle3dPartyChangeOnTrailer(e);
		handleXPartyChangeOnDriver(e);
		handleXPartyChangeOn4pl(e);
	};
	const handle4plChange = (e) => {
		handle3dPartyChangeOnTruck(e);
		handle3dPartyChangeOnTrailer(e);
		handleXPartyChangeOnDriver(e);
	};

	const transKey = `${prefix}_3rd_party`;
	const curr_company_ids =
		isNullUndEmpty(values.company_id) &&
		props.multiData.recordData.state["company_id"] & MultiChangesStateEnum.IS_INIT_DIFF
			? props.multiData.recordData.multiValues["company_id"]
			: [values.company_id];
	const transIsDisable = curr_company_ids.some((x) => isNullUndEmpty(x));
	const transIsEmptyDisable = transIsDisable && isNullUndEmpty(values[transKey]);

	return (
		<ETAETDFormGroup {...props} prefix={prefix}>
			{/* Row 1: Date, Time, Arrived, Slot time, Port */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput xs={6} sm={3}>
					<TerminalDatesInput
						component={DateInput}
						source={`${prefix}_date`}
						validate={[
							isIntoPlukkStorage
								? requiredIfNoFieldValue(`${dateRequiredValueKeyPrefix}_date`)
								: requiredMulti,
						]}
						onChange={handleEtaEtdSync}
					/>
				</GridInput>
				<GridInput xs={6} sm={3}>
					<TerminalDatesInput component={TimeInput} source={`${prefix}_time`} resettable />
				</GridInput>
				<Grid item xs={12} sm={1} ref={arrivedPopupAnchorEl}>
					<ArrivedPopup
						{...arrivedPopupProps}
						render={({ openPopup }) => (
							<ResponsiveIconButton
								onClick={openPopup}
								title={`${prefix.toUpperCase()} Arrived`}
								icon={<ArrivedIcon fontSize="small" />}
								disabled={arrivedPopupProps.disabled}
							/>
						)}
					/>
				</Grid>
				<GridInput xs={6} sm={3}>
					<TerminalDatesInput component={TimeInput} source={`${prefix}_slot_time`} resettable />
				</GridInput>
				<GridInput
					xs={6}
					sm={2}
					component={PortInput}
					source={portKey}
					hidden={isAddMode}
					filter={byTerminalFilter}
					resettable
				/>
			</FormGroupGrid>

			{/* Row 2: Transporters, Place, Trip number, Loading order */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					component={TransporterInput}
					source={`${prefix}_3rd_party`}
					prefix={prefix}
					isEmptyDisable={transIsEmptyDisable}
					excludeIds={curr_company_ids}
					disabled={transIsDisable}
					onChange={handle3dPartyChange}
					resettable
					hidden={isFieldHidden(`${prefix}_3rd_party`)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					component={TransporterInput}
					source={`${prefix}_4th_party`}
					prefix={prefix}
					excludeIds={[values.company_id, values[`${prefix}_3rd_party`]]}
					disabled={true}
					onChange={handle4plChange}
					hidden={isFieldHidden(`${prefix}_4th_party`)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					isDefaultTextMode
					component={SelectTextInput}
					sourceText={`place_${placePrefix}`}
					sourceSelect={`${prefix}_external_terminal_id`}
					reference="external-terminal-links"
					optionText="external_name"
					filter={externalTerminalFilter}
					sort={{ field: "external_name", order: "ASC" }}
					validate={[validatePlaceLength]}
					disabledSelect={externalTerminalDisabled}
					allowEmpty
					resettable
					hidden={isFieldHidden(`place_${placePrefix}`)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					component={CountryISOCodeInput}
					source={`place_${placePrefix}_country_code`}
					hidden={isFieldHidden(`place_${placePrefix}_country_code`)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={2}
					component={YesNoInput}
					source={prefix === "eta" ? "seal" : "plumb"}
					defaultValue={false}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.SEAL : HideableOrderField.PLUMB)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={4}
					component={TextInput}
					source={prefix === "eta" ? "seal_number" : "plumb_number"}
					resettable
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.SEAL_NUMBER : HideableOrderField.PLUMB_NUMBER)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					component={TripNumberInput}
					source={prefix === "eta" ? "incoming_trip_number" : "outgoing_trip_number"}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.INCOMING_TRIP_NUMBER : HideableOrderField.OUTGOING_TRIP_NUMBER)}
					resettable
				/>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					component={LoadingOrderInput}
					source={prefix === "eta" ? "unloading_sequence" : "loading_sequence"}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.UNLOADING_SEQUENCE : HideableOrderField.LOADING_SEQUENCE)}
					resettable
				/>
			</FormGroupGrid>

			{/* Row 3: Truck, Trailer, Driver, Phone, Show doc, NFSA doc */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput
					xs={12}
					sm={6}
					md={prefix === "eta" ? 6 : 4}
					lg={prefix === "eta" ? 3 : 2}
					component={SelectTextInput}
					label={"resources.orders.fields." + prefix + "_truck"}
					sourceText={`${prefix}_truck`}
					sourceSelect={`${prefix}_truck_id`}
					reference="trucks"
					source3rdParty={`${prefix}_3rd_party`}
					filter={currentTransportFilter}
					sort={currentTransportSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					isMultiDataParent={true}
					resettable
					hidden={isFieldHidden(`${prefix}_truck`)}
				/>
				<GridInput
					xs={12}
					sm={6}
					md={prefix === "eta" ? 6 : 4}
					lg={prefix === "eta" ? 3 : 2}
					component={SelectTextInput}
					label={"resources.orders.fields." + prefix + "_trailer"}
					sourceText={`${prefix}_trailer`}
					sourceSelect={`${prefix}_trailer_id`}
					reference="trailers"
					source3rdParty={`${prefix}_3rd_party`}
					filter={currentTransportFilter}
					sort={currentTransportSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					isMultiDataParent={true}
					resettable
					hidden={isFieldHidden(`${prefix}_trailer`)}
				/>
				<GridInput
					xs={12}
					sm={6}
					md={4}
					lg={2}
					component={SelectTextInput}
					sourceText={`${prefix}_driver`}
					sourceSelect={`${prefix}_driver_id`}
					reference="drivers"
					optionText={(record) =>
						`${record.first_name} ${record.last_name} | ${formatPhoneNumber(record.phone)}`
					}
					filter={currentDriverFilter}
					sort={currentDriverSort}
					disabled={isDisabledForEditing}
					isMultiDataParent={true}
					resettable
					hidden={isFieldHidden(`${prefix}_driver`)}
				/>
				<GridInput
					xs={12}
					sm={6}
					md={4}
					lg={2}
					component={PhoneInput}
					source={`${prefix}_driver_phone`}
					disabled={isDisabledForEditing}
					resettable
					hidden={isFieldHidden(`${prefix}_driver_phone`)}
				/>
				<GridInput
					xs={6}
					sm={6}
					md={4}
					lg={2}
					component={YesNoInput}
					source={`${prefix}_show_doc_for_trans`}
					label={"resources.orders.fields." + prefix + "_show_doc"}
					defaultValue={true}
					disabled={!isDisabledForEditing}
					hidden={isFieldHidden(`${prefix}_show_doc_for_trans`)}
				/>
				{prefix === "etd" && (
					<GridInput
						xs={6}
						sm={6}
						md={4}
						lg={2}
						component={YesNoInput}
						source="is_doc_for_nfsa"
						defaultValue={false}
						onChange={onIsDocForNfsaChange}
						hidden={isFieldHidden(HideableOrderField.IS_DOC_FOR_NFSA)}
					/>
				)}
			</FormGroupGrid>

			{/* Row 4: Goods & Handling (ETA) or Storage allocation (ETD) + Assignee */}
			<FormGroupGrid>
				{prefix === "eta" ? (
					<>
						<GridInput
							xs={6}
							sm={4}
							md={3}
							lg={2}
							hidden={
								values.service === OrderService.reloadCarCar ||
								isFieldHidden(HideableOrderField.COOLER_TYPE_ID)
							}
						>
							<CoolerTypeSelect filter={{ order_service: values.service }} uppercase />
						</GridInput>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							source="pallets"
							min={0}
							max={100}
							step={10}
							disabled={isColdStorageService || isOutgoingPlukk}
							resettable
							hidden={isFieldHidden(HideableOrderField.PALLETS)}
						/>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							source="boxes"
							min={0}
							max={100}
							step={5}
							disabled={isColdStorageService || isOutgoingPlukk}
							resettable
							hidden={isFieldHidden(HideableOrderField.BOXES)}
						/>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							type="float"
							digitsCount={2}
							source="kilos"
							min={0}
							max={1000}
							step={100}
							disabled={isColdStorageService || isOutgoingPlukk}
							resettable
							hidden={isFieldHidden(HideableOrderField.KILOS)}
						/>
						<GridInput
							xs={6}
							sm={4}
							md={3}
							lg={2}
							component={CommodityInput}
							source="commodity_id"
							hidden={isFieldHidden(HideableOrderField.COMMODITY_ID)}
						/>
						{!isColdStorageService && (
							<GridInput
								xs={6}
								sm={4}
								md={3}
								lg={2}
								component={OSFCUserSelectInput}
								source="assignee_1"
								resettable
								hidden={isFieldHidden(HideableOrderField.ASSIGNEE_1)}
							/>
						)}
					</>
				) : (
					<>
						{(isIntoPlukkStorage || isCrossDock) && (
							<>
								<GridInput
									xs={6}
									sm={4}
									md={2}
									lg={2}
									source={order_storage_place_field}
									label={`resources.orders.fields.${order_storage_place_field}`}
									onChange={handleAreaChange}
									resettable
								>
									<PlaceSelectInput terminalId={values["terminal_id"]} />
								</GridInput>
								{!usesStorageModule ? (
									<GridInput
										xs={4}
										sm={3}
										md={2}
										lg={2}
										component={YesNoInput}
										source="is_area_blocked"
										disabled={!values.area_id}
										hidden={isFieldHidden(HideableOrderField.IS_AREA_BLOCKED)}
									/>
								) : null}
							</>
						)}
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={PrintedDocsInput}
							source="printed_doc"
							defaultValue={false}
							hidden={isFieldHidden(HideableOrderField.PRINTED_DOC)}
						/>
						{(isIntoPlukkStorage || isCrossDock) && (
							<>
								<GridInput
									xs={4}
									sm={3}
									md={2}
									lg={2}
									component={SelectEnumInput}
									onChange={handleLabelSourceChange}
									options={{ format: "" }}
									source="label_source"
									key={"label_source"}
									enumName="label_source"
									enumObj={LabelSource}
									hidden={isFieldHidden(HideableOrderField.LABEL_SOURCE)}
								/>
								<GridInput
									xs={4}
									sm={3}
									md={2}
									lg={2}
									component={NumberInputEx}
									options={{ format: "" }}
									source="label"
									disabled={!values["label_source"]}
									resettable
									hidden={isFieldHidden(HideableOrderField.LABEL)}
								/>
							</>
						)}
						<GridInput
							xs={6}
							sm={4}
							md={2}
							lg={2}
							component={OSFCUserSelectInput}
							source="assignee_2"
							resettable
							hidden={isFieldHidden(HideableOrderField.ASSIGNEE_2)}
						/>
						{etdThirdAssigneeServices.includes(values.service) && (
							<GridInput
								xs={6}
								sm={4}
								md={2}
								lg={2}
								component={OSFCUserSelectInput}
								source="assignee_3"
								resettable
								hidden={isFieldHidden(HideableOrderField.ASSIGNEE_3)}
							/>
						)}
					</>
				)}
			</FormGroupGrid>

			{/* Row 5: All assignees for Cold Storage (ETA only) */}
			{isColdStorageService && prefix === "eta" && (
				<FormGroupGrid>
					<GridInput
						xs={6}
						sm={4}
						md={4}
						lg={4}
						component={OSFCUserSelectInput}
						source="assignee_1"
						resettable
						hidden={isFieldHidden(HideableOrderField.ASSIGNEE_1)}
					/>
					<GridInput
						xs={6}
						sm={4}
						md={4}
						lg={4}
						component={OSFCUserSelectInput}
						source="assignee_2"
						resettable
						hidden={isFieldHidden(HideableOrderField.ASSIGNEE_2)}
					/>
					<GridInput
						xs={6}
						sm={4}
						md={4}
						lg={4}
						component={OSFCUserSelectInput}
						source="assignee_3"
						resettable
						hidden={isFieldHidden(HideableOrderField.ASSIGNEE_3)}
					/>
				</FormGroupGrid>
			)}
		</ETAETDFormGroup>
	);
};

const resetVal = undefined;

const getDiffKeys = (obj1, obj2) => {
	const keys = union(Object.keys(obj1), Object.keys(obj2));
	return keys.filter((x) => obj1[x] !== obj2[x]);
};

const fieldRelation = {
	terminal_id: ["port_in", "prot_out", "storage_place_id", "area_id"],
	eta_3rd_party: [
		"eta_driver_id",
		"eta_truck_id",
		"eta_trailer_id",
		"eta_driver",
		"eta_truck",
		"eta_trailer",
	],
	etd_3rd_party: [
		"etd_driver_id",
		"etd_truck_id",
		"etd_trailer_id",
		"etd_driver",
		"etd_truck",
		"etd_trailer",
	],
	company_id: ["eta_3rd_party", "etd_3rd_party"],
};
fieldRelation["company_id"] = [
	...fieldRelation["company_id"],
	...fieldRelation["eta_3rd_party"],
	...fieldRelation["etd_3rd_party"],
];

export const FormManager = ({ isAddMode, initRecordData, ...props }) => {
	const form = useForm();
	const { permissions } = usePermissions();

	const [isTerminalDisabled, setIsTerminalDisabled] = React.useState(true);
	const [canChangeCustomerAndReference, setCanChangeCustomerAndReference] = React.useState(false);
	const [enabledFields, setEnabledFields] = React.useState();

	const onFormChange = (formState, a, b) => {
		console.log(formState);
	};

	const { values, initialValues } = useFormState({ onChange: onFormChange });
	const { isFieldHidden } = useTerminalFieldVisibility(values?.terminal_id);
	const { data: terminals } = useTerminalsContext();
	const usesStorageModule = isStorageModuleEnabled(terminals[values["terminal_id"]]);
	const [recordData, seRecordData] = React.useState(initRecordData);
	const dataProvider = useDataProvider();
	const translate = useTranslate();
	const isRankDisabled = checkIsRankDisabled(values);
	const storageAllocationTitle = translate(
		usesStorageModule
			? "resources.orders.block-label.preparation-allocation"
			: "resources.orders.block-label.storage-allocation"
	);

	const rankChoices = React.useMemo(() => rankChoicesGetter(props.record), [props.record]);

	const isColdStorageService =
		initRecordData?.multiValues["service"]?.length > 0 &&
		initRecordData.multiValues["service"].every((x) => x === OrderService.coldStorage);
	const isIntoPlukkStorage =
		initRecordData?.multiValues["service"]?.length > 0 &&
		initRecordData.multiValues["service"].every((x) => x === OrderService.intoPlukkStorage);
	const isOutgoingPlukk =
		usesStorageModule &&
		initRecordData?.multiValues["service"]?.some(
			(x, i) => x === OrderService.intoPlukkStorage && !!initRecordData.multiValues["etd_date"]?.[i]
		);
	const hasCompletedOrders = initRecordData?.multiValues?.["status"]?.some(
		(x) => x === OrderStatus.ORDER_COMPLETED
	);

	React.useEffect(() => {
		if (initRecordData && initRecordData.multiValues && initRecordData.multiValues["status"]) {
			const _canChangeTerminal = initRecordData.multiValues["status"].every((x) =>
				OrderStatusEditOrderTerminal.includes(x)
			);
			setIsTerminalDisabled(!_canChangeTerminal);

			const _canChangeCustomerAndReference = initRecordData.multiValues["status"].every((x) =>
				OrderStatusEditOrderCustomerAndReference.includes(x)
			);
			setCanChangeCustomerAndReference(_canChangeCustomerAndReference);

			// Only admin or a user with the "Can re-open the order" setting can change status of ORDER_COMPLETED orders, others - blocked completely
			if (hasCompletedOrders) {
				if (permissions?.role === UserRole.admin || permissions?.can_edit_completed_order) {
					setEnabledFields({ status: true });
				} else {
					setEnabledFields({});
				}
			}
		}
	}, [initRecordData, permissions]);

	React.useEffect(() => {
		seRecordData((prev) => {
			const diffKeys = getDiffKeys(prev.values, values);
			const newState = { ...prev.state };

			for (const key of diffKeys) {
				const isDiff = isMultiDiff(prev.initialValues[key], values[key], prev.state[key]);
				if (isDiff) newState[key] |= MultiChangesStateEnum.IS_DIFF;
				else newState[key] &= ~MultiChangesStateEnum.IS_DIFF;

				if (fieldRelation[key]) {
					fieldRelation[key].forEach((x) => {
						if (isDiff) newState[x] |= MultiChangesStateEnum.NOT_REVERT;
						else newState[x] &= ~MultiChangesStateEnum.NOT_REVERT;
					});
				}
			}

			const newObj = {
				...prev,
				state: newState,
				values,
			};
			return newObj;
		});
	}, [values]);

	React.useEffect(() => {
		seRecordData((prev) => ({
			...prev,
			initialValues,
		}));
	}, [initialValues]);

	const onChangeTest = (val) => {};

	const on3dPartyRest = (prefix) => {
		form.change(`${prefix}_driver`, resetVal);
		form.change(`${prefix}_driver_id`, resetVal);
		form.change(`${prefix}_truck`, resetVal);
		form.change(`${prefix}_truck_id`, resetVal);
		form.change(`${prefix}_trailer`, resetVal);
		form.change(`${prefix}_trailer_id`, resetVal);
	};

	const onCompanyChange = (val) => {
		if (val) {
			dataProvider.getOne("customer-companies", { id: val }).then((data) => {
				form.change("commodity_id", data.data.commodity_id);
				form.change("plumb", data.data.plumb);
				form.change("seal", data.data.seal);
				form.change("terminal_id", data.data.terminal_id);
				onTerminalChange(data.data.terminal_id);
				form.change("set_order_completed", data.data.set_order_completed);
				form.change("is_copy_documents", data.data.copy_docs_on_clone);
			});
		} else {
			form.change("commodity_id", null);
			// form.change("area_id", null);
			form.change("plumb", false);
			form.change("seal", false);
			form.change("terminal_id", resetVal);
			onTerminalChange(resetVal);
			form.change("set_order_completed", false);
			form.change("is_copy_documents", false);
		}
		form.change("eta_3rd_party", resetVal);
		form.change("etd_3rd_party", resetVal);
		on3dPartyRest("eta");
		on3dPartyRest("etd");
	};

	const onTerminalChange = (val) => {
		form.change("port_in", resetVal);
		form.change("port_out", resetVal);
		form.change("storage_place_id", resetVal);
		form.change("area_id", resetVal);
		form.change("is_area_blocked", false);
		form.change("is_per", false);
	};

	React.useEffect(() => {
		if (isAddMode && props.record && props.record.company_id) {
			dataProvider.getOne("customer-companies", { id: props.record.company_id }).then((data) => {
				form.change("is_copy_documents", data.data.copy_docs_on_clone);
			});
		}
	}, [isAddMode, props.record]);

	const onIsDocForNfsaChange = React.useCallback(
		(item) => {
			const englishText = "\nDocuments MUST be obtained from the Norwegian Food Safety Authority";
			const norwegianText = "\nDokumenter MÅ hentes på Mattilsynet";
			const old_internal_notes_text = values.internal_notes ? values.internal_notes : "";
			const newValueInternalNotes = item.target.value
				? old_internal_notes_text + englishText + norwegianText
				: old_internal_notes_text.replace(englishText + norwegianText, "");
			form.change("internal_notes", newValueInternalNotes);
		},
		[values.internal_notes, form]
	);

	if (!permissions) return null;

	const handleLabelSourceChange = (event) => {
		const val = event.target.value;
		if (!val) {
			form.change("label", null);
		} else if (val === LabelSource.pallets) {
			form.change("label", values.pallets);
		} else if (val === LabelSource.boxes) {
			form.change("label", values.boxes);
		}
	};

	const handleAreaChange = (event) => {
		if (usesStorageModule) return;
		if (event.target.value) {
			form.change("is_area_blocked", true);
		} else {
			form.change("is_area_blocked", false);
		}
	};

	const handleCbMulti = (type, key) => {
		switch (type) {
			case "revert":
				form.change(key, initialValues[key]);
				switch (key) {
					case "company_id":
						handleCbMulti("revert", "terminal_id");
						handleCbMulti("revert", "commodity_id");
						handleCbMulti("revert", "plum");
						handleCbMulti("revert", "seal");
						handleCbMulti("revert", "set_order_completed");
						handleCbMulti("revert", "is_copy_documents");
						handleCbMulti("revert", "eta_3rd_party");
						handleCbMulti("revert", "etd_3rd_party");
						break;
					case "terminal_id":
						handleCbMulti("revert", "storage_place_id");
						handleCbMulti("revert", "area_id");
						handleCbMulti("revert", "port_in");
						handleCbMulti("revert", "port_out");
						break;
					case "storage_place_id":
					case "area_id":
						break;
					case "eta_3rd_party":
						handleCbMulti("revert", "eta_driver");
						handleCbMulti("revert", "eta_driver_id");
						handleCbMulti("revert", "eta_truck");
						handleCbMulti("revert", "eta_truck_id");
						handleCbMulti("revert", "eta_trailer");
						handleCbMulti("revert", "eta_trailer_id");
						break;
					case "etd_3rd_party":
						handleCbMulti("revert", "etd_driver");
						handleCbMulti("revert", "etd_driver_id");
						handleCbMulti("revert", "etd_truck");
						handleCbMulti("revert", "etd_truck_id");
						handleCbMulti("revert", "etd_trailer");
						handleCbMulti("revert", "etd_trailer_id");
						break;
					default:
						break;
				}
				break;
			case "clear":
				form.change(key, undefined);
				break;
			default:
				break;
		}
	};

	const multiData = { recordData: recordData, cbMulti: handleCbMulti };

	const requiredMulti = (value, values, opt) => {
		if (recordData.values && recordData.initialValues) {
			const key = opt["name"];
			const isDiff = isMultiDiff(recordData.initialValues[key], value, recordData.state[key]);

			if (isDiff) {
				if (isNullUndEmpty(value)) return "Required";
			}
		}
	};

	const requiredValidArr = [requiredMulti];
	const isStatusDraft = values.status === OrderStatus.DRAFT;

	const statusAllowedIds = (
		hasCompletedOrders && permissions?.role !== UserRole.admin && !permissions?.can_edit_completed_order
			? [OrderStatus.ORDER_COMPLETED]
			: Object.values(OrderStatus)
	).filter((s) => s !== OrderStatus.DRAFT);
	return (
		<GridForm {...props} enabledFields={enabledFields} multiData={multiData}>
			<FormGroupGrid>
				{canChangeCustomerAndReference ? (
					<GridInput
						source="company_id"
						sm={2}
						component={CompanySelect}
						filter={{ no_transporters: true }}
						validate={!isStatusDraft ? requiredValidArr : undefined}
						allowEmpty={isStatusDraft}
						onChange={onCompanyChange}
					/>
				) : (
					<GridInput source="company_id" sm={2} component={CompanyText} disabled={true} />
				)}
				<GridInput
					source="reference"
					sm={2}
					component={canChangeCustomerAndReference ? OrderReferenceInput : ReadOnlyInput}
					disabled={!canChangeCustomerAndReference}
					validate={!isStatusDraft ? requiredValidArr : undefined}
				/>
				<GridInput
					sm={1}
					source="terminal_id"
					reference="terminal"
					disabled={isTerminalDisabled}
					validate={requiredValidArr}
					onChange={onTerminalChange}
				>
					<ReferenceInput>
						<SelectInput optionText="name" />
					</ReferenceInput>
				</GridInput>
				<GridInput
					source="service"
					sm={1}
					component={ServiceInput}
					validate={!isStatusDraft ? requiredValidArr : undefined}
					allowEmpty={isStatusDraft}
					defaultValue={!isStatusDraft ? OrderService.reloadCarCar : undefined}
					disabled={!isAddMode && isColdStorageService}
					isCouldStorageVisible={isAddMode || isColdStorageService}
				/>
				<GridInput
					sm={1}
					component={YesNoInput}
					source="is_per"
					defaultValue={false}
					hidden={
						isFieldHidden(HideableOrderField.IS_PER) ||
						!perEligibleServices.includes(values.service)
					}
				/>
				<GridInput sm={2} hidden={isAddMode} source="status" reference="order-statuses">
					<ReferenceInput>
						<SelectDisabledInput allowedIds={statusAllowedIds} optionText="name" />
					</ReferenceInput>
				</GridInput>
				<GridInput sm={1} component={YesNoInput} source="priority" defaultValue={false} />
				<GridInput
					sm={1}
					component={YesNoInput}
					source="rank"
					disabled={isRankDisabled}
					choices={rankChoices}
				/>

				<GridInput
					sm={1}
					component={YesNoInput}
					source="set_order_completed"
					defaultValue={false}
				/>
			</FormGroupGrid>

			<ETAETDBlock
				sm={12}
				md={12}
				lg={6}
				isAddMode={isAddMode}
				requiredMulti={requiredMulti}
				prefix="eta"
				title="ETA  —  ARRIVAL"
				permissions={permissions}
				{...props}
				enabledFields={enabledFields}
				multiData={multiData}
				isFieldHidden={isFieldHidden}
				isColdStorageService={isColdStorageService}
				isOutgoingPlukk={isOutgoingPlukk}
				handleAreaChange={handleAreaChange}
				handleLabelSourceChange={handleLabelSourceChange}
			/>
			{!isColdStorageService ? (
				<ETAETDBlock
					sm={12}
					md={12}
					lg={6}
					isAddMode={isAddMode}
					requiredMulti={requiredMulti}
					prefix="etd"
					title="ETD  —  DEPARTURE"
					permissions={permissions}
					{...props}
					enabledFields={enabledFields}
					multiData={multiData}
					isFieldHidden={isFieldHidden}
					isColdStorageService={isColdStorageService}
					isOutgoingPlukk={isOutgoingPlukk}
					handleAreaChange={handleAreaChange}
					handleLabelSourceChange={handleLabelSourceChange}
					onIsDocForNfsaChange={onIsDocForNfsaChange}
				/>
			) : (
				<ColdStorageSection
					sm={12}
					md={12}
					lg={6}
					isAddMode={isAddMode}
					isMultiChanges={true}
					{...props}
				/>
			)}

			{!isColdStorageService && (
				<>
					<FormGroupGrid
						xs={12}
						md={6}
						lg={3}
						title={translate("resources.orders.block-label.split-loads")}
						hidden={isFieldHidden(HideableOrderField.SPLIT_LOAD)}
					>
						<GridInput
							xs={4}
							component={YesNoInput}
							source="split_load"
							defaultValue={false}
							hidden={isFieldHidden(HideableOrderField.SPLIT_LOAD)}
						/>
					</FormGroupGrid>

					<ExtraWorkSection isFieldHidden={isFieldHidden} isMultiChanges />
				</>
			)}

			<FormGroupGrid>
				<GridInput
					sm={12}
					md={6}
					component={TextInput}
					source="notes"
					multiline
					rows={6}
					validate={[maxLength(1024)]}
					resettable
				/>
				<GridInput
					sm={12}
					md={6}
					className="red-border"
					component={DangerTextInputForInternalNotes}
					source="internal_notes"
					multiline
					rows={6}
					validate={[maxLength(1000)]}
					disabled
				/>
			</FormGroupGrid>
		</GridForm>
	);
};
